import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetShipmentQuery,
  useUpdateShipmentMutation,
} from "../../../graphql/generated";
import { editShipment } from "../../../redux/slices/shipment/ShipmentForm.slice";
import {
  NewShipmentInputData,
  ShipmentLocationInputData,
} from "../../../redux/slices/Types";
import EditShipmentForm from "./EditShipmentForm";
import { omit, uniqBy } from "lodash";
import { v4 as uuid } from "uuid";
import { showDialog } from "../../../redux/slices/alert/Alert.slice";
import { formatInTimeZone } from "date-fns-tz";
import { fallbackTimezone } from "../../../utils/labels/formatDateTime";
import { RootState } from "../../../redux/store";
import LoadingOverlay from "../../common/LoadingOverlay";
import { getPredefinedFromRecurrence } from "../ShipmentForm/ShipmentRecurrenceForm/ShipmentRecurrenceForm";
import { useTranslation } from "react-i18next";
import { ShipmentView } from "../../../utils/shipment/shipmentView";

const hasInvalidCoordinates = (location?: {
  latitude?: number | null;
  longitude?: number | null;
} | null) =>
  !location ||
  ((!location.latitude || location.latitude === 0) &&
    (!location.longitude || location.longitude === 0));

export const toShipmentInputData = (
  shipment: ShipmentView,
): NewShipmentInputData => {
  const convertedShipmentLocations: ShipmentLocationInputData[] = (
    shipment.shipmentLocations || []
  ).map((location) => {
    // TTMSD-582: Auto-refresh coordinates from shipper/receiver profile
    // when the stored location has invalid {0, 0} coordinates
    // (e.g., EDI orders where geocoding failed).
    let enrichedLocation = location.location;
    let enrichedAddressLabel = location.addressLabel;
    let enrichedAddressTimezone = location.addressTimezone;
    if (hasInvalidCoordinates(location.location)) {
      const profile = location.shipper || location.receiver;
      if (profile?.address?.coordinates) {
        enrichedLocation = profile.address.coordinates;
        enrichedAddressLabel = profile.address.label || enrichedAddressLabel;
        enrichedAddressTimezone =
          profile.addressTimezone || enrichedAddressTimezone;
      }
    }

    return {
      ...location,
      _id: location._id || uuid(),
      location: enrichedLocation,
      addressLabel: enrichedAddressLabel,
      addressTimezone: enrichedAddressTimezone,
      receivedGoods: (location.receivedGoods || []).map((receivedGood) =>
        omit(receivedGood, "supplier"),
      ),
      receiver: location.receiver?._id,
      shippedGoods: (location.shippedGoods || []).map((good) =>
        omit(good, "supplier"),
      ),
      shipper: location.shipper?._id,
    };
  });

  const firstLocationWithTimeWindow = shipment.shipmentLocations.filter(
    (location) => location.timeWindows.length,
  )[0];

  return {
    ...shipment,
    documents: (shipment.documents || []).map((document) => ({
      ...document,
      receiver: document.receiver?._id,
      shipper: document.shipper?._id,
    })),
    notes: shipment.notes?.map((note) => ({
      ...note,
      receiver: note.receiver?._id,
      shipper: note.shipper?._id,
    })),
    charges: shipment.charges.map((charge) => ({
      ...omit(charge, "total"),
      billingRule: charge.billingRule?._id,
      ratingTable: charge.ratingTable?._id,
    })),
    customer: shipment?.customer?._id,
    shipmentLocations: convertedShipmentLocations,
    id: shipment._id,
    date: firstLocationWithTimeWindow
      ? formatInTimeZone(
          new Date(firstLocationWithTimeWindow.timeWindows[0].fromDate),
          firstLocationWithTimeWindow.shipper?.addressTimezone ||
            firstLocationWithTimeWindow.receiver?.addressTimezone ||
            fallbackTimezone,
          "yyyy-MM-dd",
        )
      : "",
    predefinedRecurrence: getPredefinedFromRecurrence(shipment.recurrence),
    route: null,
  };
};

type EditShipmentFormContainerProps = {
  onLoad?: (shipment: ShipmentView) => void;
};

const EditShipmentFormContainer = ({
  onLoad,
}: EditShipmentFormContainerProps) => {
  const { t } = useTranslation(["orders", "common"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const shipmentQuery = useGetShipmentQuery(
    { id: id || "" },
    {
      refetchOnWindowFocus: false,
    },
  );
  const shipment: ShipmentView | undefined =
    shipmentQuery.data?.shipmentById;
  const dispatch = useDispatch();
  const stateShipment = useSelector(
    (state: RootState) => state.shipmentReducer.shipment,
  );
  useEffect(() => {
    if (shipment) {
      dispatch(editShipment(toShipmentInputData(shipment)));
    }
  }, [shipment, dispatch]);

  useEffect(() => {
    if (shipment) {
      onLoad?.(shipment);
    }
  }, [shipment, onLoad]);
  const { mutateAsync: updateShipment, isLoading: updatingShipment } =
    useUpdateShipmentMutation({});

  if (!stateShipment.id) {
    return <LoadingOverlay loading />;
  }

  return (
    <EditShipmentForm
      loading={
        isUploadingDocuments || updatingShipment || shipmentQuery.isLoading
      }
      isChildLoad={!!shipment?.parentShipment}
      isSplit={!!shipment?.isSplit}
      isFromSplit={!!shipment?.isFromSplit}
      onSubmit={async (_shipment) => {
        try {
          if (!id || !_shipment) {
            return;
          }
          // if (newDocumentsToUpload) {
          //   setIsUploadingDocuments(true);
          //   await Promise.all(
          //     newDocumentsToUpload.map(async (d) => {
          //       await fetch(d.url, {
          //         method: "PUT",
          //         body: d.file,
          //       });
          //     })
          //   );
          //   setIsUploadingDocuments(false);
          // }
          // Deduplicate by goodId+storageFacilityId to allow same commodity in multiple tanks
          const deduplicatedShipmentLocations = _shipment.shipmentLocations.map(
            (location) => ({
              ...location,
              receivedGoods: location.receivedGoods
                ? uniqBy(
                    [...location.receivedGoods].reverse(),
                    (rg: {
                      goodId: string;
                      storageFacilityId?: string | null;
                    }) => `${rg.goodId}-${rg.storageFacilityId || ""}`,
                  ).reverse()
                : [],
            }),
          );

          await updateShipment({
            editShipmentData: {
              ...omit(_shipment, [
                "_id",
                "status",
                "shipmentNumber",
                "tripId",
                "reasonForCancellation",
                // `dispatcher` is a read-only resolved field (the dispatcher's
                // name, derived server-side from `createdBy`). It is not part of
                // ShipmentUpdateInput, so sending it back rejects the mutation.
                "dispatcher",
              ]),
              charges: (_shipment.charges || []).map((charge) =>
                omit(charge, "total", "_id"),
              ),
              shipmentLocations: deduplicatedShipmentLocations,
              expenses: (_shipment.expenses || []).map((expense) =>
                omit(expense, "_id"),
              ),
            },
            id,
          });
          navigate(`/orders/details/${id}`);
        } catch (error) {
          dispatch(
            showDialog({
              type: "error",
              title: t(
                "error.createShipmentError",
                "Error while creating shipment",
              ),
              description:
                (error as Error).message ||
                t("common:error.unknownError", "Unknown error"),
            }),
          );
        } finally {
          setIsUploadingDocuments(false);
        }
      }}
    />
  );
};

export default EditShipmentFormContainer;
